import Button from '@/components/content/Button'
import { UploadExcelResult } from '@/services/deliveryAreas/types'
import { HStack, Modal, VStack } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import { FormField } from '@/components/content/FormField'
import styled from 'styled-components'
import { colors, colorSystem } from '@/styles/theme'
import MToast from '@/utils/modal/MToast'
import { useRef } from 'react'

type Props = {
  data: UploadExcelResult
  onUpload: (file: File) => void
  onClose: () => void
}

export function UploadResultModal({ data, onUpload, onClose }: Props) {
  const isAllSuccess = data.totalCount === data.successCount
  const isAllFail = data.failCount === data.totalCount

  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleCopy = () => {
    const header = '우편번호\t실패사유'
    const text = [header, ...data.updateFailItems.map((item) => `${item.zipcode}\t${item.failMessage}`)].join('\n')
    navigator.clipboard.writeText(text)
    MToast.show('복사되었습니다.')
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (file) {
      onUpload(file)
      setTimeout(() => {
        if (fileInputRef.current) fileInputRef.current.value = ''
      }, 0)
      onClose()
    }
  }

  return (
    <>
      <input ref={fileInputRef} hidden type="file" accept=".xlsx, .xls, .csv" onChange={handleFileChange} />

      {isAllSuccess && (
        <Modal open onClose={onClose}>
          <VStack gap={24} align="center">
            <Text kind="Headline_20_bold">모든 항목이 정상적으로 업로드되었습니다.</Text>
            <FormField label="성공">
              <Text kind="Body_14_regular">{data.successCount}건</Text>
            </FormField>
            <Button fit kind="primary_light" type="button" onClick={onClose}>
              확인
            </Button>
          </VStack>
        </Modal>
      )}

      {isAllFail && (
        <Modal open onClose={onClose}>
          <VStack gap={24} align="center">
            <VStack gap={8} align="center">
              <Text kind="Headline_20_bold">업로드에 실패했어요. 다시 시도해 주세요.</Text>
              <Text kind="Body_14_regular" color={colorSystem.text.common.secondary}>
                반복적으로 실패할 경우, 잠시 후 다시 시도해 주세요.
              </Text>
            </VStack>
            <HStack gap={8}>
              <Button fit kind="secondary" type="button" onClick={onClose}>
                취소
              </Button>
              <Button fit kind="primary_light" type="button" onClick={handleUploadClick}>
                재업로드
              </Button>
            </HStack>
          </VStack>
        </Modal>
      )}

      {!isAllSuccess && !isAllFail && (
        <Modal
          size="md"
          open
          onClose={onClose}
          title="일부 항목 실패"
          buttons={[
            {
              kind: 'secondary',
              content: '재업로드',
              onClick: handleUploadClick,
            },
            {
              kind: 'primary_light',
              content: '확인',
              onClick: onClose,
            },
          ]}
        >
          <Text kind="Body_14_regular">
            일부 항목이 업로드 되지 않았습니다.
            <br />
            성공한 항목은 정상적으로 등록되었으며, 아래 내역을 확인한 후 실패 건을 수정해 다시 업로드해 주세요.
          </Text>
          <ResultBox>
            <HStack style={{ height: 44, padding: '0px 16px' }} justify="space-between" align="center">
              <HStack gap={12} align="center">
                <Text kind="Body_12_bold" color={colorSystem.text.common.error}>
                  실패 {data.failCount}건
                </Text>
                <Divider height="8px" />
                <Text kind="Body_12_bold" color={colorSystem.text.common.success}>
                  성공 {data.successCount}건
                </Text>
              </HStack>
              <Button fit kind="secondary" size="sm" type="button" onClick={handleCopy}>
                복사
              </Button>
            </HStack>
            <HStack style={{ background: colorSystem.bg.table.column }} align="center">
              <Label kind="Body_12_medium" color={colorSystem.text.table.column}>
                우편번호
              </Label>
              <Divider height="16px" />
              <Content kind="Body_12_medium" color={colorSystem.text.table.column}>
                실패사유
              </Content>
            </HStack>
            <div style={{ height: 206, overflow: 'auto' }}>
              {data.updateFailItems?.map((item) => (
                <HStack>
                  <Label kind="Body_12_regular">{item.zipcode}</Label>
                  <Content kind="Body_12_regular">{item.failMessage}</Content>
                </HStack>
              ))}
            </div>
          </ResultBox>
        </Modal>
      )}
    </>
  )
}

const ResultBox = styled.div`
  margin-top: 16px;
  border: 1px solid ${colorSystem.border.table};
  border-radius: 8px;
`

const Divider = styled.div<{ height: string }>`
  width: 1px;
  height: ${({ height }) => height};
  background-color: ${colors.gray300};
`

const Label = styled(Text)`
  width: 74px;
  margin: 8px;
`

const Content = styled(Text)`
  flex: 1;
  margin: 8px;
`
